/**
 * Essentially setTimeout using a promise instead of callbacks. This can
 * be used to insert a wait into a promise chain or to wait before starting
 * a promise chain.
 *
 * @example
 * wait(1000).then(() => {
 *   // I waited 1000 ms
 * })
 */
export function wait(ms) {
    return new Promise((resolve) => {
        setTimeout(resolve, ms);
    });
}
/**
 * Wraps a promise and rejects after n milliseconds if that promise
 * has not resolved yet. If the promise resolves sometime after the timeout
 * then the results will be ignored.
 *
 * @example
 * timeout(returnsPromise(), 1000)
 *   .then((result) => {
 *     // Result of returnsPromise resolution
 *   })
 *   .catch((error) => {
 *     if (error.message === 'timeout') {
 *       // returnsPromise() Took too long
 *     } else {
 *       // Something else errored
 *     }
 *   })
 */
export function timeout(promise, ms) {
    return new Promise((resolve, reject) => {
        promise.then(resolve).catch(reject);
        setTimeout(() => reject(new Error("timeout")), ms);
    });
}
/**
 * Creates a deferred promise which allows for resolving or rejecting outside
 * of the Promise constructor executor.
 *
 * @example
 * const p = defer()
 *
 * p.then(() => {
 *
 * })
 * .catch((error) => {
 *   error.message // 'Rejected
 * })
 *
 * p.reject(new Error('Rejected'))
 */
export function defer() {
    let res = () => { };
    let rej = () => { };
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const promise = new Promise((resolve, reject) => {
        res = resolve;
        rej = reject;
    });
    promise.resolve = res;
    promise.reject = rej;
    return promise;
}
/**
 * Chain a series of operations, such that each subsequent operation
 * only starts after the prior one completes. The operations can be
 * anything, but you need to pass a transform to convert it to a promise.
 */
export function chain(operations, transform) {
    return operations.reduce((promise, operation) => promise.then(() => transform(operation)), Promise.resolve());
}
export function isFulfilled(result) {
    return result.status === "fulfilled";
}
